import React, { memo, useEffect, useState } from "react";

import { ReactCodeMirrorRef } from "@uiw/react-codemirror";
import { twJoin } from "tailwind-merge";

import { CodeInput } from "src/base-components/CodeInput/CodeInput";
import {
  ALL_OPERATOR_OPTIONS,
  OperatorDropdown,
} from "src/base-components/OperatorDropdown";
import { DecisionTableOperators } from "src/clients/flow-api";
import { isUnaryOperator } from "src/decisionTableNode/ConditionEditor/utils";
import { LastFieldRef } from "src/matrixTableNode/FloatingCodeInputProvider";
import { SetValueRef } from "src/matrixTableNode/types";

type Props = {
  mode: "effect" | "predicate";
  trackingRef: React.RefObject<HTMLElement> | null;
  editorRef: React.RefObject<ReactCodeMirrorRef>;
  tableRef: React.RefObject<HTMLElement>;
  lastFieldRef: React.MutableRefObject<LastFieldRef | null>;
  setValueRef: SetValueRef;
  detach: (keepFocus?: boolean) => void;
};

const FloatingCodeInputComponent: React.FC<Props> = ({
  mode,
  trackingRef,
  editorRef,
  tableRef,
  lastFieldRef,
  setValueRef,
  detach,
}) => {
  const [localStringValue, setLocalStringValue] = useState<string>("");
  const [localOperator, setLocalOperator] = useState<DecisionTableOperators>(
    DecisionTableOperators.EQ,
  );
  const isUnary = isUnaryOperator(localOperator);

  const containerStyle = (() => {
    if (!trackingRef?.current) {
      if (tableRef.current) {
        const rect = tableRef.current.getBoundingClientRect();
        return {
          left: rect.left,
          top: rect.top,
          width: 180,
          height: 32,
        };
      } else {
        return {
          left: -100,
          top: -100,
          width: 180,
          height: 32,
        };
      }
    }

    const boundingRect = trackingRef.current.getBoundingClientRect();

    return {
      left: boundingRect.left,
      top: boundingRect.top - 1,
      width: boundingRect.width + 1,
      height: boundingRect.height + 2,
    };
  })();

  useEffect(() => {
    setValueRef.current = {
      setValue: setLocalStringValue,
      setOperator: setLocalOperator,
    };
  }, [setValueRef]);

  return (
    <div
      className={twJoin(
        "fixed flex border [&_.cm-theme]:min-w-full",
        mode === "effect" ? "border-green-100" : "border-gray-100",
        mode === "predicate" &&
          "[&_.cm-theme]:border-indigo-600 [&_.cm-theme]:shadow-codeInput",
        trackingRef?.current ? "z-[49]" : "pointer-events-none -z-50",
      )}
      style={containerStyle}
      onKeyDown={(e) => {
        if (e.key === "Tab") {
          e.preventDefault();
          detach(true);
        }
        if (e.key === "Escape" || e.key === "Enter") {
          detach(true);
        }
      }}
    >
      <CodeInput
        ref={editorRef}
        bgColor={mode === "effect" ? "bg-green-50" : undefined}
        readOnly={isUnary}
        value={localStringValue}
        variant="cell-expandable"
        onChange={(codeInputValue) => {
          setLocalStringValue(codeInputValue);
          if (lastFieldRef.current) {
            lastFieldRef.current = {
              ...lastFieldRef.current,
              value: codeInputValue,
            };
          }
        }}
      >
        <div className={twJoin(mode === "predicate" ? "" : "w-0 opacity-0")}>
          <OperatorDropdown
            dataLoc="floating-operator-dropdown"
            options={ALL_OPERATOR_OPTIONS}
            value={localOperator}
            onChange={(operator) => {
              const [newOperator, newValue] = [
                operator as DecisionTableOperators,
                isUnaryOperator(operator as DecisionTableOperators)
                  ? ""
                  : localStringValue,
              ];

              setLocalOperator(newOperator);
              setLocalStringValue(newValue);

              if (lastFieldRef.current) {
                lastFieldRef.current = {
                  ...lastFieldRef.current,
                  operator: operator as DecisionTableOperators,
                  value: newValue,
                };
              }
            }}
          />
        </div>
      </CodeInput>
    </div>
  );
};

export const FloatingCodeInput = memo(FloatingCodeInputComponent);
